import { useEffect, useState } from 'react';
import {
  Shield,
  ShieldOff,
  Cloud,
  HardDrive,
  EyeOff,
  FolderOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useVault } from '@/hooks/useVault';
import { estimateContextChars } from '@/lib/workspace/prompt';
import type { SettingsTab } from './SettingsModal';

function StatusChip({
  onClick,
  title,
  className,
  children,
}: {
  onClick?: () => void;
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  const Comp = onClick ? 'button' : 'span';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={title}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] border transition-colors',
        onClick && 'hover:border-[#3a3a3a] cursor-pointer',
        className,
      )}
    >
      {children}
    </Comp>
  );
}

function formatRelative(ts: number, now: number): string {
  const diff = Math.max(0, now - ts);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * Vault / history / project status chips for the sidebar footer.
 */
export function WorkspaceStatusChips({
  onOpenSettings,
  compact = false,
}: {
  onOpenSettings: (tab?: SettingsTab) => void;
  /** Icon-only stack for collapsed sidebar */
  compact?: boolean;
}) {
  const { settings, activeProject } = useWorkspace();
  const { unlocked: vaultUnlocked, hasVault } = useVault();
  const contextStats = estimateContextChars(activeProject, settings);
  // Read from workspace settings only (updated on publish/pull via updateSettings)
  const syncLastPublishedAt = settings.syncLastPublishedAt;
  const syncLastPulledAt = settings.syncLastPulledAt;

  // Tick so "just now" / "5m ago" stay honest without a full remount
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const openPrivacy = () => onOpenSettings('privacy');

  const vaultChip = !hasVault ? (
    <StatusChip
      onClick={openPrivacy}
      title="Local encryption is off — click to enable"
      className="border-[#2a2a2a] text-[#5a5a5a]"
    >
      <ShieldOff className="w-3 h-3 shrink-0" />
      {!compact && <span>unencrypted</span>}
    </StatusChip>
  ) : vaultUnlocked ? (
    <StatusChip
      onClick={openPrivacy}
      title="Vault unlocked for this session — lock when stepping away. Data is sealed in IndexedDB when locked."
      className="border-green-900/40 text-green-500/70"
    >
      <Shield className="w-3 h-3 shrink-0" />
      {!compact && <span>unlocked</span>}
    </StatusChip>
  ) : (
    <StatusChip
      onClick={openPrivacy}
      title="Vault locked — unlock to access sealed data"
      className="border-yellow-900/40 text-yellow-500/70"
    >
      <Shield className="w-3 h-3 shrink-0" />
      {!compact && <span>locked</span>}
    </StatusChip>
  );

  const historyChip =
    settings.historyMode === 'ephemeral' ? (
      <StatusChip
        onClick={openPrivacy}
        title="Ephemeral — new messages are not written to disk"
        className="border-yellow-900/40 text-yellow-500/70"
      >
        <EyeOff className="w-3 h-3 shrink-0" />
        {!compact && <span>ephemeral</span>}
      </StatusChip>
    ) : settings.historyMode === 'sync' ? (
      <StatusChip
        onClick={openPrivacy}
        title={
          syncLastPublishedAt || syncLastPulledAt
            ? [
                syncLastPublishedAt
                  ? `Last publish ${new Date(syncLastPublishedAt).toLocaleString()}`
                  : null,
                syncLastPulledAt
                  ? `Last pull ${new Date(syncLastPulledAt).toLocaleString()}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : 'Sync — open Privacy to publish/pull encrypted snapshots'
        }
        className="border-sky-900/40 text-sky-400/70"
      >
        <Cloud className="w-3 h-3 shrink-0" />
        {!compact && (
          <>
            <span>sync</span>
            {syncLastPublishedAt ? (
              <span className="text-sky-500/50">
                · {formatRelative(syncLastPublishedAt, now)}
              </span>
            ) : syncLastPulledAt ? (
              <span className="text-sky-500/50">
                · pulled {formatRelative(syncLastPulledAt, now)}
              </span>
            ) : null}
          </>
        )}
      </StatusChip>
    ) : (
      <StatusChip
        onClick={openPrivacy}
        title="Local only — history stays in this browser"
        className="border-[#2a2a2a] text-[#5a5a5a]"
      >
        <HardDrive className="w-3 h-3 shrink-0" />
        {!compact && <span>local</span>}
      </StatusChip>
    );

  if (compact) {
    return (
      <div className="flex flex-col items-center gap-1.5 py-1">
        {vaultChip}
        {historyChip}
      </div>
    );
  }

  return (
    <div className="px-1.5 pb-1 flex flex-wrap gap-1">
      {activeProject && (
        <StatusChip
          title={`Project “${activeProject.name}” — instructions, memory & context on the project hub`}
          className="border-[#222] text-[#6a6864] max-w-full"
        >
          <FolderOpen className="w-3 h-3 shrink-0 opacity-70" />
          <span className="truncate max-w-[100px]">{activeProject.name}</span>
          {contextStats.items > 0 && (
            <span className="text-[#3a3a3a] shrink-0">· {contextStats.items}</span>
          )}
        </StatusChip>
      )}
      {vaultChip}
      {historyChip}
    </div>
  );
}
